import CancelIcon from '@mui/icons-material/Cancel';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import HistoryIcon from '@mui/icons-material/History';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { Box, Button, Chip, CircularProgress, Divider, Grid, InputAdornment, LinearProgress, ListItemIcon, ListItemText, Menu, MenuItem, Stack, TextField, Tooltip } from '@mui/material';
import FilterChip from 'components/FilterChip';
import { MouseEvent, useEffect, useMemo, useState } from 'react';
import UserResources from '../components/UserResources';
import { useUserResourcesQueryState } from '../hooks/useUserResourcesQueryState';

type PendingFilter = 'scope' | 'cancelled' | 'deleted' | null;

const PLATFORM_OPTIONS = [
  { label: 'Azure', value: 'azure' },
  { label: 'IoT', value: 'iot' },
  { label: 'OCI', value: 'oci' },
  { label: 'Azure DevOps', value: 'azuredevops' },
  { label: 'vSphere', value: 'vsphere' },
  { label: 'Confluent', value: 'confluent' }
];

const STATUS_FACET_OPTIONS = [
  { label: 'Modified', value: 'modified' },
  { label: 'Created', value: 'created' },
  { label: 'Imported', value: 'imported' },
  { label: 'Not tracked', value: 'not-tracked' }
];

const formatResourceTypeLabel = (value: string): string => {
  const normalized = value.trim();
  if (!normalized) {
    return '';
  }

  if (/^[a-z0-9]{1,4}$/i.test(normalized)) {
    return normalized.toUpperCase();
  }

  return normalized
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const statusLabelMap: Record<string, string> = STATUS_FACET_OPTIONS.reduce((acc, item) => {
  acc[item.value] = item.label;
  return acc;
}, {} as Record<string, string>);

function UserResourcesPage() {
  const allDays = 10 * 365;
  const {
    queryState,
    setScope,
    setResourceFilter,
    togglePlatformFilter,
    toggleResourceTypeFilter,
    toggleStatusFilter,
    setShowCancelled,
    setShowDeleted,
    setExpandAll,
    clearFacetFilters,
    removeFacetValue,
    resetAllFilters
  } = useUserResourcesQueryState(PLATFORM_OPTIONS.map(option => option.value));

  const [loading, setLoading] = useState<boolean>(false);
  const [loadAll, setLoadAll] = useState<boolean>(false);
  const [pendingFilter, setPendingFilter] = useState<PendingFilter>(null);
  const [debouncedResourceFilter, setDebouncedResourceFilter] = useState<string>('');
  const [filterMenuAnchor, setFilterMenuAnchor] = useState<null | HTMLElement>(null);
  const [availableResourceTypes, setAvailableResourceTypes] = useState<string[]>([]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedResourceFilter(queryState.resourceFilter || '');
    }, 180);

    return () => {
      clearTimeout(handler);
    };
  }, [queryState.resourceFilter]);

  useEffect(() => {
    if (!loading) {
      setPendingFilter(null);
    }
  }, [loading]);

  const handleShowSucceededResourcesFilterChange = (checked: boolean) => {
    setPendingFilter('cancelled');
    setShowCancelled(checked);
  };

  const handleShowDeletedResourcesFilterChange = (checked: boolean) => {
    setPendingFilter('deleted');
    setShowDeleted(checked);
  };

  const handleAllResourcesToggle = (checked: boolean) => {
    setPendingFilter('scope');
    setScope(checked ? 'scoped' : 'all');
  };

  const openFilterMenu = (event: MouseEvent<HTMLElement>) => {
    setFilterMenuAnchor(event.currentTarget);
  };

  const closeFilterMenu = () => {
    setFilterMenuAnchor(null);
  };

  const activeFacetCount = queryState.platformFilters.length + queryState.resourceTypeFilters.length + queryState.statusFilters.length;
  const hasResourceFilter = Boolean(queryState.resourceFilter.trim());

  const activeFacetChips = useMemo(() => {
    const platformChips = queryState.platformFilters.map(value => ({
      key: `platform-${value}`,
      label: PLATFORM_OPTIONS.find(option => option.value === value)?.label || value,
      facet: 'platform' as const,
      value
    }));

    const typeChips = queryState.resourceTypeFilters.map(value => ({
      key: `type-${value}`,
      label: formatResourceTypeLabel(value),
      facet: 'resourceType' as const,
      value
    }));

    const statusChips = queryState.statusFilters.map(value => ({
      key: `status-${value}`,
      label: statusLabelMap[value] || value,
      facet: 'status' as const,
      value
    }));

    return [...platformChips, ...typeChips, ...statusChips];
  }, [queryState.platformFilters, queryState.resourceTypeFilters, queryState.statusFilters]);

  return (
    <div className="content-surface">
      <div className="sticky-header">
        <Grid container alignItems="center" spacing={2} sx={{ px: 1, py: 1 }}>
          <Grid item xs={12} lg={7}>
            <Stack direction="row" spacing={1.25} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 0.75 }}>
              <Box component="h3" className="page-title" sx={{ m: 0, color: 'text.primary' }}>
                Resources
              </Box>
              <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 0.75 }}>
                <FilterChip
                  label="Show only my resources"
                  checked={queryState.scope === 'scoped'}
                  onChange={handleAllResourcesToggle}
                  icon={loading && pendingFilter === 'scope' ? <CircularProgress size={16} /> : <FilterListIcon />}
                  disabled={loading && pendingFilter === 'scope'}
                />
                <FilterChip
                  label="Cancelled"
                  checked={queryState.showCancelled}
                  onChange={handleShowSucceededResourcesFilterChange}
                  icon={loading && pendingFilter === 'cancelled' ? <CircularProgress size={16} /> : <CancelIcon />}
                  disabled={loading && pendingFilter === 'cancelled'}
                />
                <FilterChip
                  label="Deleted"
                  checked={queryState.showDeleted}
                  onChange={handleShowDeletedResourcesFilterChange}
                  icon={loading && pendingFilter === 'deleted' ? <CircularProgress size={16} /> : <DeleteIcon />}
                  disabled={loading && pendingFilter === 'deleted'}
                />
                <FilterChip
                  label={`${queryState.expandAll ? 'Collapse' : 'Expand'} all`}
                  checked={queryState.expandAll}
                  onChange={setExpandAll}
                  icon={queryState.expandAll ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
                />
                <Tooltip title={loadAll ? 'Showing resources from all time' : 'By default, only resources from the last 30 days are shown'}>
                  <span>
                    <FilterChip
                      label={loadAll ? 'Showing all' : 'Load all'}
                      checked={loadAll}
                      onChange={() => setLoadAll(true)}
                      icon={<HistoryIcon />}
                      disabled={loadAll}
                    />
                  </span>
                </Tooltip>
              </Stack>
            </Stack>
          </Grid>

          <Grid item xs={12} lg={5}>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', alignItems: 'center', justifyContent: { xs: 'flex-start', lg: 'flex-end' }, rowGap: 1 }}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={openFilterMenu}
                startIcon={<FilterListIcon />}
                endIcon={<KeyboardArrowDownIcon />}
                sx={{
                  minWidth: { xs: '100%', sm: 170 },
                  height: 37,
                  px: 1.25,
                  fontSize: '0.80rem',
                  textTransform: 'none',
                  borderColor: activeFacetCount > 0 ? 'primary.main' : 'rgba(0, 0, 0, 0.23)',
                  color: activeFacetCount > 0 ? 'primary.dark' : 'text.primary',
                  backgroundColor: activeFacetCount > 0 ? 'primary.50' : 'transparent'
                }}
              >
                {activeFacetCount > 0 ? `Filters (${activeFacetCount})` : 'Filters'}
              </Button>

              <Button
                variant="text"
                color="inherit"
                disabled={!hasResourceFilter && activeFacetCount === 0 && !queryState.showCancelled && !queryState.showDeleted}
                onClick={resetAllFilters}
                sx={{ textTransform: 'none', height: 37 }}
              >
                Reset all
              </Button>

              <TextField
                size="small"
                sx={{
                  minWidth: { xs: '100%', sm: 280 },
                  maxWidth: 360,
                  '& .MuiInputLabel-root': {
                    color: hasResourceFilter ? 'primary.dark' : 'text.secondary',
                    fontWeight: hasResourceFilter ? 600 : 400
                  },
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: hasResourceFilter ? 'primary.50' : 'transparent',
                    '& fieldset': {
                      borderColor: hasResourceFilter ? 'primary.main' : 'rgba(0, 0, 0, 0.23)'
                    }
                  }
                }}
                label="Filter resources"
                variant="outlined"
                value={queryState.resourceFilter}
                onChange={e => setResourceFilter(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: hasResourceFilter ? 'primary.main' : 'action.active' }} />
                    </InputAdornment>
                  )
                }}
              />
            </Stack>
          </Grid>
        </Grid>

        {activeFacetChips.length > 0 && (
          <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" sx={{ px: 1, pb: 1.1 }}>
            {activeFacetChips.map(item => (
              <Chip
                key={item.key}
                label={item.label}
                size="small"
                onDelete={() => removeFacetValue(item.facet, item.value)}
              />
            ))}
            <Button size="small" onClick={clearFacetFilters} sx={{ textTransform: 'none' }}>
              Clear facet filters
            </Button>
          </Stack>
        )}

        <Menu
          anchorEl={filterMenuAnchor}
          open={Boolean(filterMenuAnchor)}
          onClose={closeFilterMenu}
          PaperProps={{
            sx: {
              mt: 0.5,
              minWidth: 280,
              maxHeight: 460
            }
          }}
          MenuListProps={{
            dense: true,
            sx: { py: 0.5 }
          }}
        >
          <MenuItem disabled sx={{ opacity: 1 }}>
            <ListItemText primary="Platforms" primaryTypographyProps={{ fontSize: 12, fontWeight: 700, color: 'text.secondary' }} />
          </MenuItem>
          {PLATFORM_OPTIONS.map(option => {
            const selected = queryState.platformFilters.includes(option.value);
            return (
              <MenuItem
                key={option.value}
                selected={selected}
                onClick={() => togglePlatformFilter(option.value)}
                sx={{ minHeight: 32, py: 0.25, gap: 0.5 }}
              >
                <ListItemIcon sx={{ minWidth: 16, color: 'inherit' }}>
                  {selected ? <CheckIcon sx={{ fontSize: 16 }} /> : null}
                </ListItemIcon>
                <ListItemText primary={option.label} primaryTypographyProps={{ fontSize: 14 }} />
              </MenuItem>
            );
          })}

          <Divider sx={{ my: 0.5 }} />

          <MenuItem disabled sx={{ opacity: 1 }}>
            <ListItemText primary="Resource types" primaryTypographyProps={{ fontSize: 12, fontWeight: 700, color: 'text.secondary' }} />
          </MenuItem>
          {availableResourceTypes.length === 0 && (
            <MenuItem disabled>
              <ListItemText primary="No resource types available" primaryTypographyProps={{ fontSize: 13 }} />
            </MenuItem>
          )}
          {availableResourceTypes.map(resourceType => {
            const selected = queryState.resourceTypeFilters.includes(resourceType);
            return (
              <MenuItem
                key={resourceType}
                selected={selected}
                onClick={() => toggleResourceTypeFilter(resourceType)}
                sx={{ minHeight: 32, py: 0.25, gap: 0.5 }}
              >
                <ListItemIcon sx={{ minWidth: 16, color: 'inherit' }}>
                  {selected ? <CheckIcon sx={{ fontSize: 16 }} /> : null}
                </ListItemIcon>
                <ListItemText primary={formatResourceTypeLabel(resourceType)} primaryTypographyProps={{ fontSize: 14 }} />
              </MenuItem>
            );
          })}

          <Divider sx={{ my: 0.5 }} />

          <MenuItem disabled sx={{ opacity: 1 }}>
            <ListItemText primary="Statuses" primaryTypographyProps={{ fontSize: 12, fontWeight: 700, color: 'text.secondary' }} />
          </MenuItem>
          {STATUS_FACET_OPTIONS.map(option => {
            const selected = queryState.statusFilters.includes(option.value);
            return (
              <MenuItem
                key={option.value}
                selected={selected}
                onClick={() => toggleStatusFilter(option.value)}
                sx={{ minHeight: 32, py: 0.25, gap: 0.5 }}
              >
                <ListItemIcon sx={{ minWidth: 16, color: 'inherit' }}>
                  {selected ? <CheckIcon sx={{ fontSize: 16 }} /> : null}
                </ListItemIcon>
                <ListItemText primary={option.label} primaryTypographyProps={{ fontSize: 14 }} />
              </MenuItem>
            );
          })}
        </Menu>
      </div>

      {loading && (
        <Box sx={{ position: 'relative', left: -20, width: 'calc(100% + 40px)' }}>
          <LinearProgress />
        </Box>
      )}

      <UserResources
        status='all'
        sinceDays={loadAll ? allDays : undefined}
        loadAll={loadAll}
        resourceFilter={debouncedResourceFilter}
        platformFilters={queryState.platformFilters}
        resourceTypeFilters={queryState.resourceTypeFilters}
        statusFilters={queryState.statusFilters}
        showAllResourcesFilter={queryState.scope === 'all'}
        showSucceededResourcesFilter={!queryState.showCancelled}
        showDeletedResourcesFilter={!queryState.showDeleted}
        expandAll={queryState.expandAll}
        onLoadingChange={setLoading}
        onAvailableResourceTypesChange={setAvailableResourceTypes}
      />

      {!loading && !loadAll && (
        <Stack direction="row" justifyContent="center" sx={{ mt: 5, mb: 5 }}>
          <Button variant="outlined" onClick={() => setLoadAll(true)}>
            Load all (including older than 30 days)
          </Button>
        </Stack>
      )}
    </div>
  );
}

export default UserResourcesPage;
